import { Injectable } from '@angular/core';
import { FundStatsConfigKey, InjectConfig } from '@ddt-web/config-layer';
import { ColumnConfig, ColumnExtractor, ColumnText, CombinedPortfoliosMultiColumnGridExtractorConfig, ComponentData, DataServiceConstants } from '@ddt-web/data-service';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import * as cloneDeep from 'lodash/cloneDeep';
import * as groupBy from 'lodash/groupBy';
import * as isNil from 'lodash/isNil';
import * as uniqBy from 'lodash/uniqBy';

import { DATA_COLUMNS_PER_AOF } from '../config/fund-analysis-table-dimension-columns.config';
import { FULL_WIDTH, HEAVY_BORDER_LEFT, SOFT_BLACK_BORDER_LEFT, SOFT_BLACK_BORDER_RIGHT, SOFT_DASHED_BORDER_LEFT, WHITE_LEFT_BORDER } from '../constants/cell-classes';
import { aofViewLeftPinnedColumnDefs, aofViewPortfolioColumns, commonLeftColumnDefs, hiddenColumnDefaultColDef, statusViewLeftPinnedColumnDefs, statusViewPortfolioColumns } from '../constants/static-col-defs';
import { FundData } from '../models/fund-data.interface';
import { FundAreaOfFocus, FundDimension, FundStatus } from '../models/fund-stats.interface';
import { FundAnalysisDataTransformerUtil } from '../util/fund-analysis-data-transformer.util';

@Injectable({ providedIn: 'root' })
export class FundAnalysisColumnTransformerService {
	@InjectConfig(FundStatsConfigKey.STATUSES) private readonly statuses: FundStatus[] = [];
	@InjectConfig(FundStatsConfigKey.DIMENSIONS) private readonly dimensions: FundDimension[] = [];

	private heatmapColumnFields: string[] = [];

	constructor() {
		this.heatmapColumnFields = DataServiceConstants.HEAT_MAP_RANKS.map(heatmapRank => heatmapRank.categoryId);
	}

	public generateFundAnalysisTableColumns(componentData?: ComponentData, portfolioComponentData?: ComponentData, aof?: FundAreaOfFocus): Array<ColGroupDef | ColDef> {
		const aumData = portfolioComponentData?.getTabById('fund.analysis.table.portfolio.aum');
		const weightData = portfolioComponentData?.getTabById('fund.analysis.table.portfolio.weights');
		const tablePortfoliosData = portfolioComponentData?.getTabById('fund.analysis.table.portfolios');

		const numOfPortfolios: number = tablePortfoliosData?.tableData[0]?.portfolioData.filter(elem => !isNil(elem.rawValue)).length;
		const aumDataByFund: FundData[] = !!aumData ? FundAnalysisDataTransformerUtil.transformPortfolioData(aumData) : [];
		const weightDataByFund: FundData[] = !!weightData ? FundAnalysisDataTransformerUtil.transformPortfolioData(weightData) : [];

		let columnsToShow: Array<ColGroupDef | ColDef>;
		if (!!aof) {
			columnsToShow = [
				...aofViewLeftPinnedColumnDefs,
				...this.getAofViewColumns(aof),
				...this.resolvePortfolioColumns(numOfPortfolios, aumDataByFund, weightDataByFund, aofViewPortfolioColumns)
			];
		} else {
			columnsToShow = [
				...statusViewLeftPinnedColumnDefs,
				...this.transformDimensionColumnConfigsToColDefs(),
				...this.resolvePortfolioColumns(numOfPortfolios, aumDataByFund, weightDataByFund, statusViewPortfolioColumns)
			];
		}

		columnsToShow = this.attachColumnConfigs(columnsToShow, componentData, portfolioComponentData);

		return this.applySorting(numOfPortfolios, aumDataByFund, weightDataByFund, columnsToShow);
	}

	private attachColumnConfigs(columnsToShow: Array<ColDef | ColGroupDef>, componentData: ComponentData, portfolioComponentData: ComponentData): Array<ColDef | ColGroupDef> {
		const columnConfigs: { [id: string]: ColumnConfig<ColumnExtractor>[] } = groupBy(uniqBy(
			[...componentData?.tabs ?? [], ...portfolioComponentData?.tabs ?? []]
				.map(tab => tab.config.extractorConfig as CombinedPortfoliosMultiColumnGridExtractorConfig)
				.map(extractorConfig => [...extractorConfig?.otherColumns ?? [], extractorConfig?.value ?? []])
				.reduce((accumulator, array) => [...accumulator, ...array], []),
			'id'
		), 'id');

		const attachColumnConfig: (column: ColDef) => ColDef = (column: ColDef) => ({
			...column, headerComponentParams: {
				...column.headerComponentParams,
				columnConfig: columnConfigs[column.field]?.[0]
			}
		});

		return columnsToShow.map((col: ColGroupDef | ColDef) =>
			!!col['children'] ? { ...col, children: col['children'].map(column => attachColumnConfig(column)) } : attachColumnConfig(col)
		);
	}

	public generatePdfFundAnalysisTableColumns(): Array<ColGroupDef | ColDef> {
		return [...commonLeftColumnDefs, ...this.transformDimensionColumnConfigsToColDefs(false)];
	}

	/**
	 * Logic to decide which columns to show between aum, weight and no. of portfolio occurrences.
	 */
	private resolvePortfolioColumns(
		numOfPortfolios: number,
		aumDataByFund: FundData[],
		weightDataByFund: FundData[],
		lastColumns: Array<ColGroupDef | ColDef>
	): Array<ColGroupDef | ColDef> {

		let resolvedStatusViewLastColumns = cloneDeep(lastColumns);

		if (!isNil(numOfPortfolios)) {
			if (aumDataByFund.length || weightDataByFund.length) {
				const hasAllAum = aumDataByFund.every(fund => (<number>fund?.['aum']?.rawValue) > 0);
				const hasAllWeight = weightDataByFund.every(fund => (<number>fund?.['weight']?.rawValue) > 0);

				if (numOfPortfolios === 1) {
					resolvedStatusViewLastColumns = this.hideColumn(resolvedStatusViewLastColumns, 'portfolios');
					if (!hasAllWeight) {
						resolvedStatusViewLastColumns = this.hideColumn(resolvedStatusViewLastColumns, 'weight');
					}
				} else {
					resolvedStatusViewLastColumns = this.hideColumn(resolvedStatusViewLastColumns, 'weight');
				}
				if (!hasAllAum) {
					resolvedStatusViewLastColumns = this.hideColumn(resolvedStatusViewLastColumns, 'aum');
				}
				if (hasAllAum && hasAllWeight) {
					resolvedStatusViewLastColumns = this.hideColumn(resolvedStatusViewLastColumns, 'weight');
				}
			}

			return resolvedStatusViewLastColumns;
		}

		return [];
	}

	private hideColumn(columns: Array<ColGroupDef | ColDef>, fieldName: string): Array<ColGroupDef | ColDef> {
		return this.applyExtraColumnProperties(columns, fieldName, { hide: true });
	}

	private applySorting(numOfPortfolios: number, aumDataByFund: FundData[], weightDataByFund: FundData[], columns: Array<ColGroupDef | ColDef>): Array<ColGroupDef | ColDef> {
		const hasAllAum: boolean = aumDataByFund ? aumDataByFund.every(fund => (<number>fund?.['aum']?.rawValue) > 0) : false;
		const hasAllWeight: boolean = weightDataByFund ? weightDataByFund.every(fund => (<number>fund?.['weight']?.rawValue) > 0) : false;

		if (numOfPortfolios === 1 && !hasAllWeight && !hasAllAum) {
			return this.applyExtraColumnProperties(columns, 'ticker', { sort: 'asc', sortIndex: 1 });
		} else if (hasAllAum) {
			return this.applyExtraColumnProperties(columns, 'aum', { sort: 'desc', sortIndex: 1 });
		} else if (numOfPortfolios === 1 && hasAllWeight) {
			return this.applyExtraColumnProperties(columns, 'weight', { sort: 'desc', sortIndex: 1 });
		} else {
			return this.applyExtraColumnProperties(columns, 'portfolios', { sort: 'desc', sortIndex: 1 });
		}
	}

	private applyExtraColumnProperties(columns: Array<ColGroupDef | ColDef>, fieldName: string, properties: Partial<ColDef>): Array<ColGroupDef | ColDef> {
		return columns.map(col => {
			const resolvedCol = col;
			if (col?.['field'] === fieldName) {
				Object.entries(properties).forEach(([key, value]) => { resolvedCol[key] = value; });
			} else {
				const children = col?.['children']?.map(childCol => {
					if (childCol?.['field'] === fieldName) {
						Object.entries(properties).forEach(([key, value]) => { childCol[key] = value; });
					}
					return childCol;
				});
				if (children) {
					resolvedCol['children'] = children;
				}
			}
			return resolvedCol;
		});
	}

	private transformDimensionColumnConfigsToColDefs(includeDataCols: boolean = true): Array<ColGroupDef | ColDef> {
		const areasOfFocus: FundAreaOfFocus[] = [].concat(...this.statuses.map((status: FundStatus) => status.aofs).filter(aof => !!aof));

		const aofsByDimension: { [dimension: string]: FundAreaOfFocus[] } = groupBy(areasOfFocus, (aof: FundAreaOfFocus) => aof.dimension);

		return Object.entries(cloneDeep(aofsByDimension)).map(([dimensionId, aofConfigs]: [string, FundAreaOfFocus[]], dimensionIndex: number) => {
			const mergedConfigsByAofId = aofConfigs.reduce((configs, config) => {
				if (configs[config.aofId]) {
					configs[config.aofId].subAofIds.push(...config.subAofIds);
				} else {
					configs[config.aofId] = config;
				}

				return configs;
			}, {});

			const dimensionConfig: FundDimension = this.dimensions.find(dimension => dimension.dimensionId === dimensionId);
			const aofColumns: ColDef[] = Object.values(mergedConfigsByAofId).map((aofConfig: FundAreaOfFocus, index: number) => {
				const cellClass: string[] = this.getAofCellClass(index, dimensionIndex);
				const aofColDef = this.getAofColDef(aofConfig);
				return {
					...aofColDef,
					headerClass: cellClass,
					cellRendererParams: { ...aofColDef.cellRendererParams, cellClass }
				};
			});

			return {
				headerName: dimensionConfig.description,
				groupId: dimensionConfig.dimensionId,
				headerClass: ['group-header', ...(dimensionIndex === 0 ? [] : [WHITE_LEFT_BORDER])],
				headerGroupComponent: 'headerGroupComponent',
				children: includeDataCols ? this.generateAofAndCorrespondingDataColumns(aofColumns) : aofColumns
			};
		});
	}

	private generateAofAndCorrespondingDataColumns(aofColumns): Array<ColGroupDef | ColDef> {
		const aofAndCorrespondingDataColumns: Array<ColDef> = [].concat(
			...aofColumns.filter(aof => !!DATA_COLUMNS_PER_AOF[aof.field]).map((aof: ColDef) => this.getCorrespondingColsForAof(aof))
		);
		this.updateColumnsWithExtraHeatmapStyles(aofAndCorrespondingDataColumns);
		return aofAndCorrespondingDataColumns;
	}

	private getAofViewColumns(aofConfig: FundAreaOfFocus): Array<ColDef> {
		if (!aofConfig) {
			return [];
		}
		const aofColDef: ColDef = this.getAofColDef(aofConfig);
		return this.getCorrespondingColsForAof(aofColDef);
	}

	private getAofColDef(aofConfig: FundAreaOfFocus): ColDef {
		return {
			field: aofConfig.aofId,
			cellRenderer: 'statusCellComponent',
			cellRendererParams: {
				cellClass: [FULL_WIDTH]
			},
			headerComponentParams: {
				headerTooltip: new ColumnText(`FUND_ANALYSIS_${aofConfig.aofId}_TOOLTIP`),
				headerText: new ColumnText(`FUND_ANALYSIS_${aofConfig.aofId}`, aofConfig.description)
			}
		};
	}

	private getCorrespondingColsForAof(aof: ColDef): Array<ColDef> {
		const aofCorrespondingColumnConfigs: Array<ColumnConfig<ColumnExtractor>> = DATA_COLUMNS_PER_AOF[aof.field].filter(
			(columnConfig: ColumnConfig<ColumnExtractor>) => columnConfig.visible
		);
		const correspondingDataColumns: ColDef[] = aofCorrespondingColumnConfigs.map((columnConfig: ColumnConfig<ColumnExtractor>, index: number) => {
			const cellClass: string[] = this.getCellClass(index, aofCorrespondingColumnConfigs.length - 1);
			return {
				...hiddenColumnDefaultColDef,
				field: columnConfig.id,
				headerComponentParams: {
					...hiddenColumnDefaultColDef.headerComponentParams,
					headerText: columnConfig.label,
					headerTooltip: columnConfig.infoBubble
				},
				headerClass: cellClass,
				cellRendererParams: {
					cellClass
				}
			};
		});
		this.updateColumnsWithExtraHeatmapStyles(correspondingDataColumns);

		return [aof, ...correspondingDataColumns];
	}

	private updateColumnsWithExtraHeatmapStyles(columns: ColDef[]): void {
		const firstHeatmapColumnIndex: number = columns.findIndex(column => column.field === this.heatmapColumnFields[0]);
		const lastHeatmapColumnIndex: number = columns.findIndex(column => column.field === this.heatmapColumnFields[this.heatmapColumnFields.length - 1]);
		if (firstHeatmapColumnIndex > 0 && lastHeatmapColumnIndex > 0) {
			[firstHeatmapColumnIndex - 1, lastHeatmapColumnIndex].forEach((rightBorderColumnIndex: number) => {
				columns[rightBorderColumnIndex].headerClass = [
					...(columns[rightBorderColumnIndex].headerClass as any ?? []),
					SOFT_BLACK_BORDER_RIGHT
				];
				columns[rightBorderColumnIndex].cellRendererParams.cellClass = [
					...(columns[rightBorderColumnIndex].cellRendererParams.cellClass as any ?? []),
					SOFT_BLACK_BORDER_RIGHT
				];
				columns[rightBorderColumnIndex + 1].cellRendererParams.cellClass = [SOFT_BLACK_BORDER_LEFT];
				columns[rightBorderColumnIndex + 1].headerClass = [SOFT_BLACK_BORDER_LEFT];
			});
		}
	}

	private getAofCellClass(index: number, dimensionIndex: number): string[] {
		let aofColumnHeaderClass = [HEAVY_BORDER_LEFT];
		if (index !== 0) {
			aofColumnHeaderClass = [SOFT_BLACK_BORDER_LEFT];
		} else if (dimensionIndex === 0) {
			aofColumnHeaderClass = [];
		}

		return [...aofColumnHeaderClass, FULL_WIDTH];
	}

	private getCellClass(index: number, lastIndex: number): string[] {
		let cellClass = [SOFT_DASHED_BORDER_LEFT, FULL_WIDTH];
		if (index === lastIndex) {
			cellClass = [...cellClass];
		}

		return cellClass;
	}
}
